import type { DeviceState } from '@/types/device';
import type { BaselineMeasurementPayload, SpecimenMeasurementPayload } from '@/services/api/testService';
import { uploadQueueService } from '@/services/UploadQueueService';

/**
 * Maps a captured device snapshot into the DeviceStateSnapshot shape used by the API.
 * Returns undefined when no snapshot is available.
 */
export function buildDeviceStatePayload(snapshot: DeviceState | null | undefined) {
  if (!snapshot) return undefined;
  return {
    batteryLevel: snapshot.batteryLevel,
    batteryVoltage: snapshot.batteryVoltage,
    temperature: snapshot.temperature,
    humidity: snapshot.humidity,
    powerState: snapshot.powerState,
    electrodeType: snapshot.electrodeType,
    isBusy: snapshot.busy,
    progressPercent: snapshot.progress,
    warnings: snapshot.warnings,
  };
}

/**
 * Persists a measurement result to the backend using a durable write-ahead strategy:
 * the payload is written to the offline upload queue (IndexedDB) BEFORE the network
 * upload is attempted, so a refresh/crash during the (up to 30s) POST window can never
 * orphan the measurement. Retries on connectivity loss are handled by the queue plus the
 * app-boot and `online`-event sweeps.
 *
 * Non-network errors (e.g. validation failures) are rethrown so callers can
 * surface them to the user; the payload is retained in the queue for diagnostics.
 */
export async function saveMeasurementWithFallback(
  testId: string,
  phase: 'baseline' | 'specimen',
  payload: BaselineMeasurementPayload | SpecimenMeasurementPayload,
): Promise<void> {
  await uploadQueueService.saveWithWriteAhead(testId, phase, payload);
}
